/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useState } from 'react';
import API_BASE_URL from 'src/constants';
import { handleUnauthorized } from './authToken';

export interface AvailableFiltersObject {
  [key: string]: {
    operators: string[];
    type: string;
    options?: string[];
  }
}

export interface FilterCondition {
  field: string;
  operator: string;
  value: string;
  operators?: string[];
  type?: string;
}

export interface ActiveFilterObject {
  id: number;
  user_id: number;
  company_id: number | null;
  name: string;
  model_id: string;
  domain: FilterCondition[];
  active: boolean;
}

export interface FilterCreateObject {
  filter:
  | {
    conditions: FilterCondition[];
    operator: string;
  }
  | FilterCondition;
  model_id: string;
  name: string;
}

export const convertFilterDomainToObject = (domain?: [string, string, string][]): FilterCondition[] => {
  if (!domain) return []
  const filters = domain.map((e: [string, string, string]) => {
    const field = e[0];
    const operator = e[1];
    const value = e[2];
    return { field, operator, value };
  });
  return filters;
}

/**
 * Delete a filter by id for a model
 * @param model_id - model id
 * @param filter_id - filter id
 */
export const deleteFilter = async (model_id: string, filter_id: number) => {
  const accessToken = localStorage.getItem('access_token');
  try {
    const response = await fetch(`${API_BASE_URL}portal/filters/filter/${model_id}/${filter_id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete filter');
    }
  } catch (err) {
    throw new Error('Failed to delete filter');
  }
}

export const prepareFilterData = (filterData: FilterCondition[], name: string, modelId: string): FilterCreateObject | null => {
  const cleanObject = filterData.filter(filter => filter.value !== undefined && filter.value !== '');

  if (cleanObject.length === 0) {
    return null;
  }

  const body = {
    filter: {
      conditions: cleanObject
        .filter(filter => filter.value !== undefined && filter.value !== '')
        .map(filter => ({
          field: filter.field,
          operator: filter.operator,
          value: filter.value,
        })),
      operator: '&',
    },
    model_id: modelId,
    name,
  } as FilterCreateObject;

  let d = body;
  if ('conditions' in d.filter && d.filter.conditions.length === 1) {
    d = {
      filter: d.filter.conditions[0],
      model_id: modelId,
      name,
    };
  }

  return d;
};

export interface UseFiltersInterface {
  activeFilters: ActiveFilterObject[] | null;
  availableFilters: AvailableFiltersObject[] | null;
  loading: boolean;
  domain: string | undefined;
  error: string | null;
  getActiveFilters: () => Promise<void>;
  createFilter: (filterData: FilterCondition[], name: string) => Promise<void>;
  deleteFilterById: (filter_id: number) => Promise<void>;
  filter: ActiveFilterObject | null;
  setFilter: (filter: ActiveFilterObject | null) => void;
  setModelId: (model_id: string) => void;
  setDomain: (domain: string | undefined) => void;
  modelId: string;
}

const useFilters = (model_id: string): UseFiltersInterface => {
  const accessToken = localStorage.getItem('access_token');
  const [modelId, setModelId] = useState<string>(model_id);
  const [filter, setFilter] = useState<ActiveFilterObject | null>(null);
  const [domain, setDomain] = useState<string | undefined>(undefined);
  const [activeFilters, setActiveFilters] = useState<ActiveFilterObject[] | null>(null);
  const [availableFilters, setAvailableFilters] = useState<AvailableFiltersObject[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get active filters for a model 
   */
  const getActiveFilters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}portal/filters/filter/${modelId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to fetch partner data: ${response.statusText}`);
      }

      const data: {
        id: number;
        user_id: number;
        company_id: number | null;
        name: string;
        model_id: string;
        domain: Array<[string, string, string]>;
        active: boolean;
      }[] = await response.json();

      let activeFiltersTransform = [] as ActiveFilterObject[];
      if (data) {
        activeFiltersTransform = data.map((filter) => {
          return {
            ...filter,
            domain: convertFilterDomainToObject(filter.domain),
          };
        });
      }

      setActiveFilters(activeFiltersTransform);

    } catch (err) {
      setActiveFilters(null);
      setError(err instanceof Error ? err.message : 'Failed to fetch partner data');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);


  /**
   * Get available filters for a model
   */
  const getAvailableFilters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}portal/filters/option/${modelId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to fetch available filters: ${response.statusText}`);
      }

      const data: AvailableFiltersObject[] = await response.json();
      // check if data is an array
      if (!Array.isArray(data)) {
        setAvailableFilters([]);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const formattedData = Object.entries(data).flatMap(([key, value]) => {
          if (Object.keys(value).length > 1) {
            return [
              ...Object.entries(value)
                .map(([nestedKey, nestedValue]: [string, any]) => ({
                  [nestedKey]: {...nestedValue}
                })),
            ];
          }
          return {...value};
        });
        setAvailableFilters(formattedData);
      }
    } catch (err) {
      setAvailableFilters(null);
      setError(err instanceof Error ? err.message : 'Failed to fetch partner data');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  /**
   * Create a new filter
   * @param d - body of the request
   */
  const createFilter = async (filterData: FilterCondition[], name: string) => {
    setLoading(true);
    setError(null);
    try {

      // delete all filters before creating a new one
      if (filter?.id) {
        await deleteFilter(modelId, filter.id);
      }

      const d = prepareFilterData(filterData, name, modelId);
      if (!d) {
        return;
      }
      // No more conditions, delete the filter
      if ('conditions' in d.filter && d.filter.conditions.length === 0 && activeFilters && filter?.id) {
        await deleteFilter(modelId, filter.id);
      } else {
        const response = await fetch(`${API_BASE_URL}portal/filters/filter`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(d),
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleUnauthorized(accessToken);
          }
          throw new Error(`Failed to fetch partner data: ${response.statusText}`);
        }
        const r = await response.json();
        setFilter({
          ...r,
          domain: convertFilterDomainToObject(r.domain),
        })
      }



      await getActiveFilters();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch partner data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a filter by id
   * @param model_id - model id
   * @param filter_id - filter id
   */
  const deleteFilterById = async (filter_id: number) => {
    if (!model_id || !filter_id) return;
    await deleteFilter(model_id, filter_id);
    setFilter(null);
    await getActiveFilters();
  }

  useEffect(() => {
    if (!model_id) return;
    getActiveFilters();
    getAvailableFilters();
  }, []);

  return {
    activeFilters, filter, domain, availableFilters, loading, error, getActiveFilters, createFilter, deleteFilterById, setFilter, setModelId, setDomain, modelId,
  };
};

export default useFilters;
